import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "../../components/ui/sonner";
import { fmtDT } from "../../lib/date";
import { FileDown, Loader2, Paperclip, Trash2, Upload } from "lucide-react";

// 纯日期是人工补录的日历日；带时区的保存时间是实际瞬间，不截断 UTC 日期。
const usageInstant = (value) => value ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value) : null;
function durationText(start, end, zh) {
  const from = usageInstant(start);
  const to = end ? usageInstant(end) : new Date();
  if (!from || !to || Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return zh ? "时长未知" : "Duration unknown";
  if (/^\d{4}-\d{2}-\d{2}$/.test(start)) {
    // 人工补录只有日期、没有小时；按自然日计算，避免虚构精确的使用小时数。
    const fromDay = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
    const toDay = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
    const days = Math.floor((toDay - fromDay) / 86400000);
    if (days < 0) return zh ? "开始时间尚未到达" : "Start time is in the future";
    return days ? (zh ? `${days} 天` : `${days}d`) : (zh ? "不足 1 天" : "Less than 1 day");
  }
  const hours = Math.floor((to - from) / 3600000);
  if (hours < 0) return zh ? "开始时间尚未到达" : "Start time is in the future";
  const days = Math.floor(hours / 24);
  if (!hours) return zh ? "不足 1 小时" : "Less than 1 hour";
  return zh ? `${days ? `${days} 天 ` : ""}${hours % 24 ? `${hours % 24} 小时` : ""}`.trim()
    : `${days ? `${days}d ` : ""}${hours % 24 ? `${hours % 24}h` : ""}`.trim();
}

/** 资产详情：展示每次使用阶段；附件单独保存，失败时保留已经成功的文件。 */
export default function HRMSAssetDetail({ assetId, onClose, api, zh, canManageFiles }) {
  const T = useCallback((z, e) => zh ? z : e, [zh]);
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    if (!assetId) return;
    const request = ++requestRef.current;
    setLoading(true); setError("");
    try { const { data } = await api.get(`/hrms/assets/${assetId}`); if (request === requestRef.current) setAsset(data); }
    catch (e) { if (request === requestRef.current) setError(e?.response?.data?.detail || T("资产详情加载失败", "Could not load asset details")); }
    finally { if (request === requestRef.current) setLoading(false); }
  }, [api, assetId, T]);
  // 切换资产时丢弃上一条未完成的请求，避免把旧附件或旧使用记录显示到新资产。
  useEffect(() => { setAsset(null); if (assetId) load(); return () => { requestRef.current += 1; }; }, [assetId, load]);

  const upload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    setBusy(true);
    const failures = [];
    for (const file of files) {
      try {
        if (file.size > 25 * 1024 * 1024) throw new Error(T("超过 25MB", "exceeds 25MB"));
        const form = new FormData(); form.append("file", file);
        await api.post(`/hrms/assets/${assetId}/attachments`, form, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (e) { failures.push(`${file.name}: ${e?.response?.data?.detail || e.message || T("上传失败", "upload failed")}`); }
    }
    await load();
    if (failures.length) toast.error(failures.join("；"));
    else toast.success(T("附件已上传", "Attachments uploaded"));
    setBusy(false);
  };
  const download = async (attachment) => {
    try {
      const { data } = await api.get(`/hrms/assets/${assetId}/attachments/${attachment.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a"); link.href = url; link.download = attachment.name || "attachment";
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(e?.response?.data?.detail || T("附件下载失败", "Could not download attachment")); }
  };
  const remove = async (attachment) => {
    if (!window.confirm(T(`从此资产移除附件「${attachment.name}」？`, `Remove "${attachment.name}" from this asset?`))) return;
    setBusy(true);
    try {
      await api.delete(`/hrms/assets/${assetId}/attachments/${attachment.id}`);
      await load(); toast.success(T("附件已移除", "Attachment removed"));
    } catch (e) { toast.error(e?.response?.data?.detail || T("附件移除失败", "Could not remove attachment")); }
    finally { setBusy(false); }
  };

  const history = asset?.usageHistory || [];
  const periods = asset?.assigneeId ? [{ assigneeName: asset.assigneeName,
    departmentName: asset.currentDepartmentName || asset.assigneeDepartmentName,
    departmentNameEn: asset.currentDepartmentNameEn || asset.assigneeDepartmentNameEn,
    startAt: asset.usageStartAt, endAt: null }, ...history.slice().reverse()] : history.slice().reverse();
  return <Dialog open={!!assetId} onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
    <DialogContent className="max-w-2xl" closeDisabled={busy}>
      <DialogHeader><DialogTitle>{T("资产详情", "Asset details")}{asset?.name ? ` · ${asset.name}` : ""}</DialogTitle></DialogHeader>
      {loading && !asset ? <div className="py-8 text-center"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
        : error && !asset ? <div className="space-y-2 text-sm text-destructive"><p>{error}</p><button type="button" onClick={load} className="underline">{T("重试", "Retry")}</button></div>
        : asset && <div className="space-y-5 text-sm" data-testid="hrms-asset-detail">
          {error && <div className="text-destructive"><span>{error} </span><button type="button" onClick={load} className="underline">{T("重试", "Retry")}</button></div>}
          <div className="text-slate-600">{asset.category}{asset.assetTag ? ` · ${asset.assetTag}` : ""}</div>
          <section aria-labelledby="hrms-asset-history-title">
            <h3 id="hrms-asset-history-title" className="font-semibold text-slate-800">{T("使用记录", "Usage history")}</h3>
            {periods.length === 0 ? <p className="mt-2 text-slate-500">{T("暂无使用记录", "No usage history")}</p> :
              <ol className="mt-2 space-y-2" data-testid="hrms-asset-usage-history">{periods.map((period, index) => <li key={`${period.startAt || "unknown"}-${period.endAt || "current"}-${index}`} className="rounded-lg border border-slate-200 p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2"><span className="font-semibold text-slate-800">{period.assigneeName || T("未知使用人", "Unknown user")}{!period.endAt && ` · ${T("当前使用", "Current")}`}</span><span className="text-slate-600">{durationText(period.startAt, period.endAt, zh)}</span></div>
                <p className="mt-1 text-slate-500">{(zh ? period.departmentName : period.departmentNameEn || period.departmentName) || T("部门未知", "Department unknown")}{period.departmentRecordedAt && period.departmentName ? T("（变更时登记）", " (recorded at change)") : ""}</p>
                <p className="mt-1 text-slate-500">{fmtDT(period.startAt) || T("开始时间未知", "Start unknown")} – {period.endAt ? fmtDT(period.endAt) : T("至今", "Present")}</p>
              </li>)}</ol>}
          </section>
          <section aria-labelledby="hrms-asset-attachments-title">
            <div className="flex flex-wrap items-center gap-2"><h3 id="hrms-asset-attachments-title" className="font-semibold text-slate-800"><Paperclip className="mr-1 inline h-4 w-4" />{T("附件", "Attachments")}</h3>
              {canManageFiles && <><input ref={fileRef} type="file" multiple className="sr-only" onChange={upload} aria-label={T("选择资产附件", "Choose asset attachments")} />
                <button type="button" data-testid="hrms-asset-attachment-upload" disabled={busy} onClick={() => fileRef.current?.click()} className="ml-auto inline-flex min-h-10 items-center gap-1 rounded-full border border-slate-300 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{T("上传附件", "Upload")}</button></>}
            </div>
            {canManageFiles && <p className="mt-1 text-xs text-slate-500">{T("支持多个文件，单个文件不超过 25MB。", "Multiple files supported; up to 25MB each.")}</p>}
            {(asset.attachments || []).length === 0 ? <p className="mt-2 text-slate-500">{T("暂无附件", "No attachments")}</p> : <ul className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200">{asset.attachments.map((attachment) => <li key={attachment.id} className="flex items-center gap-2 px-3 py-2">
              <span className="min-w-0 flex-1 break-all text-slate-700">{attachment.name}</span>
              <button type="button" onClick={() => download(attachment)} aria-label={T(`下载 ${attachment.name}`, `Download ${attachment.name}`)} className="inline-flex min-h-10 min-w-10 items-center justify-center text-slate-600 hover:text-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"><FileDown className="h-4 w-4" /></button>
              {canManageFiles && <button type="button" disabled={busy} onClick={() => remove(attachment)} aria-label={T(`移除 ${attachment.name}`, `Remove ${attachment.name}`)} className="inline-flex min-h-10 min-w-10 items-center justify-center text-red-600 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 disabled:opacity-50"><Trash2 className="h-4 w-4" /></button>}
            </li>)}</ul>}
          </section>
        </div>}
    </DialogContent>
  </Dialog>;
}
